import { FC, HTMLProps } from 'react';
import styled from 'styled-components';
import { Label } from './Input';

type ContainerProps = {
  resizable?: boolean;
  padding?: string;
  disabled?: boolean;
  readOnly?: boolean;
} & React.HTMLProps<HTMLDivElement>;
const ContainerComponent: FC<ContainerProps> = ({ children, ...props }) => {
  return <div {...props}>{children}</div>;
};

const Container = styled(ContainerComponent)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  stroke: var(--gray-500);
  margin-left: 1px;

  textarea {
    width: 100%;
    border: 1px solid var(--gray-300);
    border-radius: 8px;
    padding: ${({ padding }) => padding || '0.8rem 1rem'};
    box-shadow: var(--box-shadow);
    transition: 0.2s;
    resize: ${({ resizable }) => (resizable ? 'vertical' : 'none')};

    background-color: white;
    color: var(--gray-700);

    &:disabled {
      background-color: var(--gray-200);
      ::placeholder {
        color: var(--gray-300);
      }
    }
    &:hover {
      border: 1px solid var(--gray-400);
    }
    &:focus {
      outline: 1px solid var(--gray-700);
    }
    &::placeholder {
      color: var(--gray-500);
      opacity: 1;
    }
    &:read-only {
      cursor: default;
      background-color: var(--gray-50);
      color: var(--gray-600);
      outline: none;
      border: 1px solid var(--gray-300);
    }
  }

  button {
    position: absolute;
    right: 1.5rem;
    width: 86px;
  }
`;

const CharacterLimitComponent: FC<{ error: number }> = (props) => (
  <div {...props} />
);
const CharacterLimit = styled(CharacterLimitComponent)`
  margin-left: auto;
  margin-right: 0;
  text-align: end;
  margin-top: 0.5rem;
  color: ${(props) => (props.error ? 'var(--error-600)' : 'var(--gray-500)')};
  font-size: 0.9rem;
`;

type Props = {
  className?: string;
  padding?: string;
  label?: string;
  handleSubmit?: () => void;
  noEnterFireSubmit?: boolean;
  characterLimit?: number;
  resizable?: boolean;
} & HTMLProps<HTMLTextAreaElement>;
const Textarea: FC<Props> = ({
  className,
  value,
  onChange,
  padding,
  label,
  handleSubmit,
  noEnterFireSubmit,
  characterLimit,
  resizable,
  ...props
}) => {
  const handleKeyPress: React.KeyboardEventHandler<HTMLTextAreaElement> = (
    e,
  ) => {
    if (e.code === 'Enter' && !e.shiftKey && !noEnterFireSubmit) {
      e.preventDefault();
      if (!isValueEmpty()) fireSubmit();
    }
  };
  const fireSubmit = () => !props.disabled && handleSubmit?.();
  const isValueEmpty = () => !(value as string)?.replace(/\s/g, '');

  return (
    <div>
      {label && <Label>{label}</Label>}
      <Container
        className={className}
        padding={padding}
        readOnly={props.readOnly}
        disabled={props.disabled}
        resizable={resizable}
      >
        <textarea
          {...props}
          onChange={onChange}
          onKeyPress={handleKeyPress}
          value={value}
        />
      </Container>
      {typeof value === 'string' && characterLimit && (
        <CharacterLimit error={value.length > characterLimit ? 1 : 0}>
          Caractères utilisés: {value.length} / {characterLimit}
        </CharacterLimit>
      )}
    </div>
  );
};

export default Textarea;
